

























































import {
  defineComponent, computed,
} from '@vue/composition-api';
import platform from '@/shared/utils/platform';
import store from '@/store';
import { useMaxQuotaText } from '@/shared/utils/getMaxQuota';
import useDispatchProcess from '@/hooks/useDispatchProcess';
import { DisplayVersionEnum } from '@/views_v2/home/types';
import { ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { RouterV2Enum } from '@/router/router-names';

/** 文本片段类型 */
interface TextSegment {
  text: string;
  isHighlight?: boolean;
}

/** 标题配置类型 */
interface TitleConfig {
  segments: TextSegment[];
  showOverdueTag?: boolean;
}

export default defineComponent({
  name: 'FinanceCardNew',
  setup(props, { root }) {
    /** 使用统一的授信流程分发 hook */
    const { creditProcessDispatch, applyProcessDispatch } = useDispatchProcess();

    /** 响应式最高额度文案 */
    const maxQuotaText = useMaxQuotaText();

    /** 授信数据 */
    const creditData = computed(() => store.state.home.creditData);

    /** 支付宝产品状态 */
    const aliProductStatus = computed(() => store.state.home.aliProductStatus);

    /** 额度数据是否就绪 */
    const isDataReady = computed(() => !!creditData.value);

    /** 是否支付宝授信失败 */
    const isAliApplyFailed = computed(() => (
      isDataReady.value
      && !!creditData.value?.displayVersion
      && creditData.value.displayVersion === DisplayVersionEnum.ALIAPPLYFAILED
    ));

    /** 是否有待还款订单 */
    const hasOrderWaitForRepay = computed(() => (
      creditData.value && Number(creditData.value.needRepayOrderNum) > 0
    ));

    /** 是否有逾期订单 */
    const hasOrderOverdue = computed(() => (
      !!creditData.value && Number(creditData.value.overdueNum) > 0
    ));

    /** 是否有可用额度 */
    const hasCredit = computed(() => (
      creditData.value
      && creditData.value.creditStatus
      && Number(creditData.value.creditAmount) > 0
    ));

    /** 业务信息中的利率信息 */
    const businessInfoCreditInfo = computed(() => (
      store.state.base.businessInfoExtra?.creditInfo || { rateFloor: '', rateCeil: '' }
    ));

    /** 格式化额度金额 */
    const formatCreditAmount = (creditAmount: string | number) => Number(creditAmount).toFixed(0);

    /**
     * 标题配置 - 集中管理所有状态的渲染逻辑
     * 新增状态只需在这里添加一个 if 分支即可，无需修改 template
     */
    const titleConfig = computed((): TitleConfig | null => {
      if (!isDataReady.value || !creditData.value) {
        return null;
      }

      // 状态1: 支付宝授信失败
      if (isAliApplyFailed.value) {
        return {
          segments: [{ text: '暂无额度可用' }],
        };
      }

      // 状态2: 有待还款订单
      if (hasOrderWaitForRepay.value) {
        return {
          segments: [
            { text: '近30天你有' },
            { text: ` ${creditData.value.needRepayOrderNum} `, isHighlight: true },
            { text: '笔待还' },
          ],
          showOverdueTag: hasOrderOverdue.value,
        };
      }

      // 状态3: 有可用额度
      if (hasCredit.value) {
        return {
          segments: [
            { text: '你有' },
            {
              text: ` ${formatCreditAmount(creditData.value.creditAmount)} `,
              isHighlight: true,
            },
            { text: ' 元可提现' },
          ],
        };
      }

      // 状态4: 无额度待申请（默认状态）
      return {
        segments: [
          { text: '您有最高' },
          { text: ` ${maxQuotaText.value.replace('万', '')} `, isHighlight: true },
          { text: '万额度待申请' },
        ],
      };
    });

    /**
     * 副标题文案 - 集中管理
     * 新增状态只需在这里添加一个 if 分支即可
     */
    const subTitleText = computed(() => {
      if (!isDataReady.value) {
        return '';
      }

      // 状态1: 支付宝授信失败
      if (isAliApplyFailed.value) {
        return '抱歉，您暂无额度可用';
      }

      // 状态2: 有待还款订单
      if (hasOrderWaitForRepay.value) {
        return '良好的习惯有助于降息提额';
      }

      // 状态3、4: 显示利率信息
      const { rateFloor, rateCeil } = businessInfoCreditInfo.value;
      return `综合年化利率（单利）${rateFloor}%-${rateCeil}%`;
    });

    /** 去还款 */
    const onRepay = () => {
      const { current, overDue } = store.state.base.repayState;
      ubtSetupPageButton(
        'finance_myProfileGoMyBillList',
        {
          current,
          overDue,
        },
      );
      root.$router.push({
        path: RouterV2Enum.myBill,
      });
    };

    /** 去提现 */
    const onApplyProduct = async () => {
      ubtSetupPageButton('finance_myProfileApplyProduct');
      await applyProcessDispatch();
    };

    /** 去授信 - 使用统一的 creditProcessDispatch */
    const onApplyCredit = async () => {
      ubtSetupPageButton('finance_myProfileApplyCredit');
      await creditProcessDispatch();
    };

    /** 操作按钮文案 */
    const actionButtonLabel = computed(() => {
      if (!isDataReady.value) return '';

      if (hasOrderWaitForRepay.value) {
        return '去还款';
      }

      if (hasCredit.value) {
        return '去提现';
      }

      // 支付宝小程序环境下，授信暂停状态存在时不显示申请额度入口
      if ((platform.isAlipayMini || platform.isDingTalk) && aliProductStatus.value?.status) {
        return '';
      }

      return '去申请';
    });

    /** 操作按钮点击事件 */
    const onActionButtonClick = () => {
      if (hasOrderWaitForRepay.value) {
        onRepay();
      } else if (hasCredit.value) {
        onApplyProduct();
      } else {
        onApplyCredit();
      }
    };

    /** 跳转到额度审批页 */
    const showPreApplyPage = () => {
      ubtSetupPageButton('finance_myProfileGoMyPreApply');
      root.$router.push({
        path: RouterV2Enum.myPreApply,
      });
    };

    /** 跳转到提现记录页 */
    const showMyApplyPage = () => {
      const { current, overDue } = store.state.base.repayState;
      ubtSetupPageButton(
        'finance_myProfileGoMyApply', {
          current,
          overDue,
        },
      );
      root.$router.push({
        path: RouterV2Enum.myApply,
        query: {
          from: 'my-profile',
        },
      });
    };

    /** 跳转到还款记录页 */
    const showRepayRecordsPage = () => {
      ubtSetupPageButton('finance_myProfileGoRepayRecords');
      root.$router.push({
        path: RouterV2Enum.repayRecords,
      });
    };

    /**
     * 入口列表配置 - 统一管理入口项
     * 新增入口只需在这里添加配置即可
     */
    const entryList = [
      {
        id: 'preApply',
        label: '额度审批',
        handler: showPreApplyPage,
      },
      {
        id: 'myApply',
        label: '提现记录',
        handler: showMyApplyPage,
      },
      {
        id: 'repayRecords',
        label: '还款记录',
        handler: showRepayRecordsPage,
      },
    ];

    return {
      titleConfig,
      subTitleText,
      isAliApplyFailed,
      actionButtonLabel,
      entryList,
      onActionButtonClick,
    };
  },
});
